import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserRound, Search, ShieldCheck, X } from "lucide-react";

import { Button } from "@/components/shared/ui/button";
import { Input } from "@/components/shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/shared/ui/dialog";
import { Badge } from "@/components/shared/ui/badge";
import { Skeleton } from "@/components/shared/ui/skeleton";
import {
  fetchImpersonatableUsers,
  impersonateUser,
  type ImpersonatableUser,
} from "@/api/impersonate";
import {
  IMPERSONATION_PROOF_KEY,
  ORIGINAL_TOKEN_KEY,
  setupAxios,
} from "@/api/base";
import { wipeQueryCache } from "@/lib/queryPersister";

const ORIGINAL_EMAIL_KEY = "originalEmail";
const IMPERSONATING_EMAIL_KEY = "impersonatingEmail";

function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export { isImpersonating } from "@/api/base";

export const startImpersonation = async (targetUserId: string) => {
  const resp = await impersonateUser(targetUserId);
  // Stash current token as "original" only if we are not already impersonating,
  // and only after the 200: a refused pick leaves staff's own session unflagged.
  if (!localStorage.getItem(ORIGINAL_TOKEN_KEY)) {
    const current = localStorage.getItem("token");
    if (current) {
      localStorage.setItem(ORIGINAL_TOKEN_KEY, current);
    }
  }
  localStorage.setItem("token", resp.authToken);
  localStorage.setItem(IMPERSONATING_EMAIL_KEY, resp.user.email);
  // A later hop's null must not leave the earlier target's proof behind.
  if (resp.impersonation) {
    localStorage.setItem(IMPERSONATION_PROOF_KEY, resp.impersonation);
  } else {
    localStorage.removeItem(IMPERSONATION_PROOF_KEY);
  }
  // Remember who "original" is — only set once at first hop.
  if (!localStorage.getItem(ORIGINAL_EMAIL_KEY)) {
    localStorage.setItem(ORIGINAL_EMAIL_KEY, resp.impersonatedBy);
  }
  setupAxios(resp.authToken);
  // Wipe persisted query cache so the impersonated user does NOT see
  // the staff member's prior cached portfolio on first paint.
  wipeQueryCache();
  window.location.assign("/");
};

export const exitImpersonation = () => {
  const orig = localStorage.getItem(ORIGINAL_TOKEN_KEY);
  if (orig) {
    localStorage.setItem("token", orig);
    setupAxios(orig);
  }
  localStorage.removeItem(ORIGINAL_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATION_PROOF_KEY);
  localStorage.removeItem(ORIGINAL_EMAIL_KEY);
  localStorage.removeItem(IMPERSONATING_EMAIL_KEY);
  // Same reason as startImpersonation: don't leak the impersonated
  // user's cached portfolio into the staff member's session.
  wipeQueryCache();
  window.location.assign("/");
};

/** Staff-only trigger button + picker dialog. Renders nothing for non-staff. */
export const ImpersonationMenuButton: React.FC<{
  userIsStaff: boolean;
  compact?: boolean;
}> = ({ userIsStaff, compact = false }) => {
  const [open, setOpen] = useState(false);
  if (!userIsStaff) return null;
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size={compact ? "icon" : "sm"}
        onClick={() => setOpen(true)}
        className="text-muted-foreground hover:text-foreground"
        aria-label="Impersonate a user"
        title="Impersonate a user"
      >
        <UserRound className="h-4 w-4" />
        {!compact && <span className="ml-2">Impersonate</span>}
      </Button>
      <ImpersonationDialog open={open} onOpenChange={setOpen} />
    </>
  );
};

const ImpersonationDialog: React.FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 250);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["impersonatable-users", debouncedQ],
    queryFn: () => fetchImpersonatableUsers(debouncedQ || undefined),
    enabled: open,
    staleTime: 30_000,
  });

  const users: ImpersonatableUser[] = useMemo(() => data ?? [], [data]);

  const onPick = async (u: ImpersonatableUser) => {
    setErr(null);
    setSubmitting(u.id);
    try {
      await startImpersonation(u.id);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Impersonation failed. Are you still signed in as staff?",
      );
      setSubmitting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Impersonate user
          </DialogTitle>
          <DialogDescription>
            Staff-only. Your current session will be replaced by the selected
            user&apos;s session. You can return to your own account from the
            banner at the top.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search by email, name, or company"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>

        {err && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {err}
          </div>
        )}

        <div className="max-h-[50vh] overflow-y-auto rounded-md border border-border">
          {isLoading && (
            <ul className="divide-y divide-border">
              {Array.from({ length: 6 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 p-3">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </li>
              ))}
            </ul>
          )}
          {!isLoading && isError && (
            <div className="p-4 text-sm text-muted-foreground">
              Couldn&apos;t load users.
            </div>
          )}
          {!isLoading && !isError && users.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">
              No matching users.
            </div>
          )}
          {!isLoading && !isError && users.length > 0 && (
            <ul className="divide-y divide-border">
              {users.map((u) => {
                const fullName = [u.firstName, u.lastName]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => onPick(u)}
                      disabled={submitting !== null}
                      className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/60 disabled:opacity-60"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-foreground">
                        {(u.firstName?.[0] || u.email[0] || "?").toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground">
                            {fullName || u.email}
                          </span>
                          {u.isSuperuser ? (
                            <Badge variant="danger">super</Badge>
                          ) : u.isStaff ? (
                            <Badge variant="info">staff</Badge>
                          ) : null}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {u.email}
                          {u.companyName ? ` · ${u.companyName}` : ""}
                          {u.position ? ` · ${u.position}` : ""}
                        </div>
                      </div>
                      {submitting === u.id && (
                        <span className="text-xs text-muted-foreground">
                          signing in…
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Top banner rendered whenever an impersonation session is active. */
export const ImpersonationBanner: React.FC = () => {
  if (typeof window === "undefined") return null;
  const originalEmail = localStorage.getItem(ORIGINAL_EMAIL_KEY);
  const impersonatingEmail = localStorage.getItem(IMPERSONATING_EMAIL_KEY);
  const hasOriginal = !!localStorage.getItem(ORIGINAL_TOKEN_KEY);
  if (!hasOriginal) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-3 border-b border-warning/40 bg-warning/10 px-4 py-2 text-xs text-warning-foreground/90"
    >
      <div className="min-w-0 truncate">
        <span className="font-semibold text-warning-foreground">
          Impersonating
        </span>{" "}
        <span className="text-foreground">
          {impersonatingEmail ?? "another user"}
        </span>
        {originalEmail && (
          <>
            {" "}
            <span className="text-muted-foreground">
              (original: {originalEmail})
            </span>
          </>
        )}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={exitImpersonation}
        className="h-7 gap-1"
      >
        <X className="h-3.5 w-3.5" />
        Exit impersonation
      </Button>
    </div>
  );
};
